'use client';

import { useCallback } from 'react';
import styles from './RichBlocksManager.module.scss';
import { IconArrowDown, IconLayoutGrid, IconPlus, IconTrash } from '@tabler/icons-react';
import { Badge, Button, Group, Menu, Text } from '@mantine/core';
import { RichBlockEditor } from '@/src/components/ArticlePublisher/RichBlockEditor';
import { richBlockTemplates } from '@/src/components/ArticlePublisher/RichBlockTemplates';
import type { RichBlock } from '@/src/types/RichBlock';

export type RichBlocksManagerProps = {
  blocks: RichBlock[];
  onChange: (blocks: RichBlock[]) => void;
  showProspectionButton?: boolean;
  title?: string;
};

export function RichBlocksManager({
  blocks,
  onChange,
  showProspectionButton = true,
  title = 'Blocs enrichis',
}: RichBlocksManagerProps) {
  const updateRichBlock = useCallback(
    (index: number, patch: Partial<RichBlock>) => {
      onChange(blocks.map((b, i) => (i === index ? ({ ...b, ...patch } as RichBlock) : b)));
    },
    [blocks, onChange]
  );

  const addRichBlock = useCallback(
    (block: RichBlock) => {
      onChange([...blocks, block]);
    },
    [blocks, onChange]
  );

  const removeRichBlock = useCallback(
    (index: number) => {
      onChange(blocks.filter((_, i) => i !== index));
    },
    [blocks, onChange]
  );

  const moveRichBlock = useCallback(
    (index: number, direction: -1 | 1) => {
      const next = [...blocks];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= next.length) {
        return;
      }
      const [moved] = next.splice(index, 1);
      next.splice(newIndex, 0, moved);
      onChange(next);
    },
    [blocks, onChange]
  );

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>
          {title} ({blocks.length})
        </h2>
        <Group gap="xs">
          {showProspectionButton && (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconLayoutGrid size={14} />}
              onClick={() =>
                onChange(
                  richBlockTemplates
                    .filter((t) =>
                      [
                        'checklist-mvp-review',
                        'comparaison-internaliser-taskforce',
                        'quiz-readiness',
                        'certificat-lecture',
                        'faq-objections-esn',
                        'graphique-time-to-value',
                        'template-confluence-action',
                      ].includes(t.id)
                    )
                    .map((t) => ({ ...t.block }))
                )
              }
            >
              Insérer les blocs prospection
            </Button>
          )}
          <Menu>
            <Menu.Target>
              <Button variant="light" size="xs" leftSection={<IconPlus size={14} />}>
                Ajouter un bloc
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() =>
                  addRichBlock({
                    type: 'chart',
                    chartType: 'bar',
                    title: '',
                    labels: [],
                    values: [],
                    caption: '',
                  })
                }
              >
                Graphique vierge
              </Menu.Item>
              <Menu.Item
                onClick={() =>
                  addRichBlock({
                    type: 'expandable',
                    title: '',
                    content: '',
                  })
                }
              >
                FAQ accordéon
              </Menu.Item>
              <Menu.Item
                onClick={() =>
                  addRichBlock({
                    type: 'imagePlaceholder',
                    prompt: '',
                    caption: '',
                  })
                }
              >
                Image placeholder
              </Menu.Item>
              <Menu.Item
                onClick={() =>
                  addRichBlock({
                    type: 'richText',
                    title: '',
                    content: '<p></p>',
                  })
                }
              >
                Zone de texte riche
              </Menu.Item>
              <Menu.Item
                onClick={() =>
                  addRichBlock({
                    type: 'confluenceTemplate',
                    title: '',
                    description: '',
                    fileName: '',
                    items: [{ text: '', checked: false }],
                  })
                }
              >
                Template Confluence
              </Menu.Item>
              <Menu.Divider />
              {richBlockTemplates.map((t) => (
                <Menu.Item key={t.id} onClick={() => addRichBlock({ ...t.block })}>
                  {t.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </div>

      {blocks.length === 0 && (
        <Text c="dimmed" size="sm" mb="md">
          Aucun bloc enrichi. Ajoutez des templates prospection ou créez un bloc vide.
        </Text>
      )}

      {blocks.map((block, index) => (
        <div key={`${block.type}-${index}`} className={styles.richBlockCard}>
          <div className={styles.richBlockHeader}>
            <Badge size="lg">{block.type}</Badge>
            <Group gap="xs">
              <Button
                variant="subtle"
                size="xs"
                disabled={index === 0}
                onClick={() => moveRichBlock(index, -1)}
              >
                <IconArrowDown size={14} style={{ transform: 'rotate(180deg)' }} />
              </Button>
              <Button
                variant="subtle"
                size="xs"
                disabled={index === blocks.length - 1}
                onClick={() => moveRichBlock(index, 1)}
              >
                <IconArrowDown size={14} />
              </Button>
              <Button variant="subtle" color="red" size="xs" onClick={() => removeRichBlock(index)}>
                <IconTrash size={14} />
              </Button>
            </Group>
          </div>
          <RichBlockEditor block={block} onChange={(patch) => updateRichBlock(index, patch)} />
        </div>
      ))}
    </div>
  );
}
